import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckSquare2,
  Clock3,
  Eye,
  Layers3,
  Link2,
  Palette,
  RefreshCw,
  RotateCcw,
  SplitSquareHorizontal,
  Square,
  Tag,
  Ungroup,
  X,
} from "lucide-react";
import { InterimTagEditorV53 } from "../components/InterimTagEditorV53";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREFS_KEY = "inksight-kanban-preferences-v1";
const LAST_USED_COLOR_KEY = "inksight-kanban-last-used-color-v1";
const INTAKE_PHASE_ID = "awaiting_graphics_approval";
const COMPLETE_PHASE_ID = "graphics_complete";

const DEFAULT_COLORS = {
  awaiting_graphics_approval: "#7c2d12",
  ready_for_submission: "#78350f",
  artwork_submitted: "#1e3a8a",
  pdf_proof: "#164e63",
  swatch: "#7c2d12",
  pilot: "#581c87",
  graphics_complete: "#064e3b",
  in_production: "#14532d",
};



const STATUS_TAG_STYLES = {
  "Graphic Accepted": "border-emerald-700/70 bg-emerald-950/50 text-emerald-300",
  "Billing Approved": "border-blue-700/70 bg-blue-950/50 text-blue-300",
  "PDF Sent": "border-cyan-700/70 bg-cyan-950/50 text-cyan-300",
  "Edits": "border-rose-700/70 bg-rose-950/50 text-rose-300",
  "PDF Approved": "border-teal-700/70 bg-teal-950/50 text-teal-300",
  "Pilot Scheduled": "border-amber-700/70 bg-amber-950/50 text-amber-300",
  "Pilot Sent": "border-purple-700/70 bg-purple-950/50 text-purple-300",
  "Pilot Approved": "border-green-700/70 bg-green-950/50 text-green-300",
};

const PROJECT_COLOR_PRESETS = [
  "#dc2626", "#ea580c", "#d97706", "#65a30d", "#059669", "#0891b2",
  "#2563eb", "#7c3aed", "#c026d3", "#db2777", "#64748b", "#334155",
];

const GROUP_CLASSES = [
  "border-l-red-500",
  "border-l-amber-500",
  "border-l-cyan-500",
  "border-l-blue-500",
  "border-l-purple-500",
  "border-l-pink-500",
];

const groupClass = (value) => GROUP_CLASSES[
  [...String(value || "")].reduce((sum, item) => sum + item.charCodeAt(0), 0) % GROUP_CLASSES.length
];

const loadPrefs = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(PREFS_KEY) || "{}");
    return {
      density: saved.density === "comfortable" ? "comfortable" : "compact",
      colors: { ...DEFAULT_COLORS, ...(saved.colors || {}) },
    };
  } catch {
    return { density: "compact", colors: { ...DEFAULT_COLORS } };
  }
};

const loadLastUsedColor = () => {
  const value = String(localStorage.getItem(LAST_USED_COLOR_KEY) || "").trim();
  return /^#[0-9a-f]{6}$/i.test(value) ? value : "";
};

// A queue hint, never an authority for archival: the server owns transitions.
const productionArchiveHint = (project) => {
  if (project.is_archived || (project.workflow_phase !== "in_production" && project.status !== "in_production")) return null;
  if (!project.production_started_at) return { label: "Archive date needs review", review: true };
  const started = new Date(project.production_started_at);
  if (Number.isNaN(started.getTime())) return { label: "Archive date needs review", review: true };
  const due = new Date(started.getTime() + 7 * 24 * 60 * 60 * 1000);
  return { label: `Archives ${due.toLocaleDateString()}`, review: false };
};

const projectCardStyle = (project) => {
  const color = String(project.kanban_color || "").trim();
  if (!color) return undefined;
  return {
    backgroundColor: `${color}24`,
    borderColor: `${color}88`,
    borderLeftColor: color,
  };
};

const waitingAge = (value) => {
  if (!value) return "Waiting for Graphics";
  const start = new Date(value).getTime();
  if (!Number.isFinite(start)) return "Waiting for Graphics";
  const elapsedHours = Math.max(0, Math.floor((Date.now() - start) / 3600000));
  if (elapsedHours < 1) return "Waiting less than 1 hour";
  if (elapsedHours < 24) return `${elapsedHours}h waiting`;
  return `${Math.floor(elapsedHours / 24)}d waiting`;
};

const identityValues = (user) => {
  const values = new Set([
    user?.id,
    user?.full_name,
    user?.username,
    user?.email,
  ].map((value) => String(value || "").trim().toLowerCase()).filter(Boolean));
  [user?.username, user?.email].forEach((value) => {
    const text = String(value || "").trim().toLowerCase();
    if (text.includes("@")) values.add(text.split("@", 1)[0]);
  });
  return values;
};

const matchesUser = (value, user) => {
  const text = String(value || "").trim().toLowerCase();
  return Boolean(text && identityValues(user).has(text));
};

const projectAssignedToUser = (project, user) => (
  matchesUser(project.isr, user)
  || matchesUser(project.osr, user)
  || matchesUser(project.created_by, user)
  || matchesUser(project.created_by_email, user)
);

const StatusTagBadge = ({ value, compact }) => {
  if (!value) return null;
  const style = STATUS_TAG_STYLES[value] || "border-slate-700 bg-slate-950/50 text-slate-300";
  return (
    <span className={`inline-flex items-center rounded-full border font-medium ${style} ${compact ? "mt-0.5 px-1.5 py-0 text-[8px] leading-4" : "mt-2 px-2 py-0.5 text-[10px]"}`}>
      {value}
    </span>
  );
};

const arrangeCards = (cards) => {
  const byGroup = new Map();
  cards.forEach((project) => {
    if (!project.kanban_group_id) return;
    const group = byGroup.get(project.kanban_group_id) || [];
    group.push(project);
    byGroup.set(project.kanban_group_id, group);
  });

  byGroup.forEach((members) => {
    members.sort((a, b) => Number(a.kanban_group_order || 0) - Number(b.kanban_group_order || 0));
  });

  const emitted = new Set();
  const arranged = [];

  cards.forEach((project) => {
    const groupId = project.kanban_group_id;
    if (!groupId) {
      arranged.push({ project, showGroupHeader: false, phaseGroupCount: 0 });
      return;
    }
    if (emitted.has(groupId)) return;
    emitted.add(groupId);
    const members = byGroup.get(groupId) || [project];
    members.forEach((member, index) => {
      arranged.push({
        project: member,
        showGroupHeader: index === 0,
        phaseGroupCount: members.length,
      });
    });
  });

  return arranged;
};

export const KanbanPageV13 = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ projects: [], phases: [], filters: {} });
  const [loading, setLoading] = useState(true);
  const [dragged, setDragged] = useState(null);
  const [prefs] = useState(loadPrefs);
  const [lastUsedColor, setLastUsedColor] = useState(loadLastUsedColor);
  const [colorProjectId, setColorProjectId] = useState(null);
  const [tagProjectId, setTagProjectId] = useState(null);
  const [savingColorProjectId, setSavingColorProjectId] = useState(null);
  const [savingTagProjectId, setSavingTagProjectId] = useState(null);
  const [busyIntakeId, setBusyIntakeId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupBusy, setGroupBusy] = useState(false);
  const { canChangeProjectStatus, canViewAllProjects, roleLabel, user, isAdmin } = useAuth();
  const [viewScope, setViewScope] = useState(searchParams.get("view") === "all" ? "all" : "mine");

  const [filters, setFilters] = useState({
    customer_id: searchParams.get("customer_id") || "",
    manufacturer_id: searchParams.get("manufacturer_id") || "",
    isr: searchParams.get("isr") || "",
    osr: searchParams.get("osr") || "",
    phase: searchParams.get("phase") || "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    ["customer_id", "manufacturer_id", "isr", "osr"].forEach((key) => {
      if (filters[key]) params.set(key, filters[key]);
    });

    try {
      const response = await axios.get(`${API}/workflow/kanban?${params}`);
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load Graphics Queue");
    } finally {
      setLoading(false);
    }
  }, [filters.customer_id, filters.manufacturer_id, filters.isr, filters.osr]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams();
    // Preserve both choices explicitly. Earlier versions represented
    // "My Projects" by removing the view parameter, which caused the
    // default-all wrappers to immediately restore view=all.
    if (canViewAllProjects) {
      params.set("view", viewScope === "all" ? "all" : "mine");
    }
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    setSearchParams(params, { replace: true });
  }, [filters, viewScope, canViewAllProjects, setSearchParams]);

  const updateFilter = (key, value) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  };

  const scopeProjects = useMemo(() => {
    if (!canViewAllProjects || viewScope === "all") return data.projects || [];
    return (data.projects || []).filter((project) => projectAssignedToUser(project, user));
  }, [canViewAllProjects, data.projects, user, viewScope]);

  const selectedProjects = useMemo(() => {
    const ids = new Set(selectedIds);
    return scopeProjects.filter((project) => ids.has(project.id));
  }, [scopeProjects, selectedIds]);

  const groupTotals = useMemo(() => {
    const totals = {};
    scopeProjects.forEach((project) => {
      if (project.kanban_group_id) {
        totals[project.kanban_group_id] = (totals[project.kanban_group_id] || 0) + 1;
      }
    });
    return totals;
  }, [scopeProjects]);

  const visiblePhases = filters.phase
    ? data.phases.filter((item) => item.id === filters.phase)
    : data.phases;
  const options = data.filters || {};
  const compact = prefs.density === "compact";
  const selectedHasGroup = selectedProjects.some((project) => project.kanban_group_id);

  const canSelectProject = (project) => (
    canChangeProjectStatus
    && project
    && project.card_type !== "intake"
    && project.workflow_phase !== COMPLETE_PHASE_ID
    && project.is_archived !== true
  );

  const toggleProjectSelection = (project) => {
    if (!canSelectProject(project)) return;
    setSelectedIds((previous) => (
      previous.includes(project.id)
        ? previous.filter((id) => id !== project.id)
        : [...previous, project.id]
    ));
  };

  const applyProjectUpdates = (projects) => {
    const updates = new Map((projects || []).map((project) => [project.id, project]));
    setData((previous) => ({
      ...previous,
      projects: previous.projects.map((project) => updates.get(project.id) || project),
    }));
  };

  const groupSelectedProjects = useCallback(async () => {
    if (groupBusy || selectedProjects.length < 2) {
      if (selectedProjects.length < 2) toast.info("Select at least two projects to create a group");
      return;
    }

    const sharedCustomer = selectedProjects.every(
      (project) => project.customer_name && project.customer_name === selectedProjects[0]?.customer_name,
    ) ? selectedProjects[0].customer_name : "";
    const defaultName = sharedCustomer ? `${sharedCustomer} Group` : "Project Group";
    const groupName = window.prompt("Group name:", defaultName);
    if (groupName === null) return;
    if (!groupName.trim()) {
      toast.error("Group name is required");
      return;
    }

    setGroupBusy(true);
    try {
      const response = await axios.post(`${API}/workflow/kanban/groups`, {
        project_ids: selectedProjects.map((project) => project.id),
        group_name: groupName.trim(),
      });
      applyProjectUpdates(response.data.projects || []);
      setSelectedIds([]);
      toast.success(`Grouped ${response.data.project_count} projects as ${response.data.group_name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to group projects");
    } finally {
      setGroupBusy(false);
    }
  }, [groupBusy, selectedProjects]);

  const ungroupSelectedProjects = useCallback(async () => {
    if (groupBusy || !selectedHasGroup) return;
    const grouped = selectedProjects.filter((project) => project.kanban_group_id);
    if (!grouped.length) return;

    setGroupBusy(true);
    try {
      const response = await axios.post(`${API}/workflow/kanban/groups/ungroup`, {
        project_ids: grouped.map((project) => project.id),
      });
      applyProjectUpdates(response.data.projects || []);
      setSelectedIds([]);
      toast.success(`Ungrouped ${response.data.project_count} project${response.data.project_count === 1 ? "" : "s"}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to ungroup projects");
    } finally {
      setGroupBusy(false);
    }
  }, [groupBusy, selectedHasGroup, selectedProjects]);

  useEffect(() => {
    const handleShortcut = (event) => {
      const tagName = String(event.target?.tagName || "").toLowerCase();
      if (["input", "select", "textarea"].includes(tagName) || event.target?.isContentEditable) return;
      if (!event.altKey) return;

      const key = event.key.toLowerCase();
      if (key === "g") {
        event.preventDefault();
        groupSelectedProjects();
      }
      if (key === "u") {
        event.preventDefault();
        ungroupSelectedProjects();
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [groupSelectedProjects, ungroupSelectedProjects]);

  const move = async (project, phase) => {
    if (
      !canChangeProjectStatus
      || !project
      || project.card_type === "intake"
      || project.workflow_phase === phase
      || phase === INTAKE_PHASE_ID
    ) return;

    try {
      const response = await axios.put(`${API}/workflow/projects/${project.id}/phase`, { phase });
      setData((previous) => ({
        ...previous,
        projects: previous.projects.map((item) => item.id === project.id ? response.data : item),
      }));
      toast.success(`Moved to ${response.data.workflow_phase_label}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to move project");
    }
    setDragged(null);
  };

  const setProjectColor = async (project, color) => {
    if (!canChangeProjectStatus || !project || project.card_type === "intake") return;
    setSavingColorProjectId(project.id);
    try {
      const response = await axios.put(`${API}/workflow/projects/${project.id}/kanban-color`, { color });
      setData((previous) => ({
        ...previous,
        projects: previous.projects.map((item) => item.id === project.id ? response.data : item),
      }));
      if (color) {
        localStorage.setItem(LAST_USED_COLOR_KEY, color);
        setLastUsedColor(color);
      }
      toast.success(color ? "Project color updated" : "Project color reset to automatic");
      if (!color) setColorProjectId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update project color");
    } finally {
      setSavingColorProjectId(null);
    }
  };

  const setStatusTag = async (project, tag) => {
    if (!canChangeProjectStatus || !project || project.card_type === "intake") return;
    setSavingTagProjectId(project.id);
    try {
      const response = await axios.put(`${API}/workflow/projects/${project.id}/status-tag`, { tag });
      setData((previous) => ({
        ...previous,
        projects: previous.projects.map((item) => item.id === project.id ? response.data : item),
      }));
      toast.success(tag ? `Status tag set to ${tag}` : "Status tag cleared");
      setTagProjectId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status tag");
    } finally {
      setSavingTagProjectId(null);
    }
  };

  // graphics_admin_kanban_archive_v30
  const archiveSelectedProjects = async () => {
    const archiveable = selectedProjects.filter(
      (project) => project.card_type !== "intake"
    );

    if (!isAdmin || archiveable.length === 0) return;

    const noun = archiveable.length === 1 ? "project" : "projects";

    if (
      !window.confirm(
        `Archive ${archiveable.length} selected ${noun}? Files and history will be retained.`
      )
    ) {
      return;
    }

    setGroupBusy(true);

    try {
      for (const project of archiveable) {
        await axios.post(
          `${API}/project-archive/${project.id}/archive`
        );
      }

      toast.success(
        `Archived ${archiveable.length} ${noun}`
      );

      setSelectedIds([]);
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail
          || "Failed to archive selected projects"
      );
    } finally {
      setGroupBusy(false);
    }
  };

  const createProjects = async (intakeCard) => {
    if (!window.confirm("Approve this request and create one Graphics project per SKU?")) return;
    setBusyIntakeId(intakeCard.id);
    try {
      const response = await axios.post(
        `${API}/intake-submissions/${intakeCard.id}/convert-v4`,
        { split_by_sku: true },
      );
      const count = response.data.created_count || response.data.group_count || 0;
      toast.success(`Created ${count} project${count === 1 ? "" : "s"}`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create Graphics projects");
    } finally {
      setBusyIntakeId(null);
    }
  };

  if (loading) {
    return <div className="animate-pulse text-muted-foreground">Loading Graphics Queue...</div>;
  }

  return (
    <div className={compact ? "space-y-3" : "space-y-6"}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Graphics Queue</h1>
          <p className="text-muted-foreground mt-1">
            {canViewAllProjects && viewScope === "all"
              ? `All projects for ${roleLabel}`
              : `My projects for ${roleLabel}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canViewAllProjects && (
            <div className="inline-flex rounded-lg border border-border bg-card p-1">
              <button type="button" onClick={() => setViewScope("mine")} className={`rounded-md px-3 py-1.5 text-sm ${viewScope === "mine" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>My Projects</button>
              <button type="button" onClick={() => setViewScope("all")} className={`rounded-md px-3 py-1.5 text-sm ${viewScope === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>All Projects</button>
            </div>
          )}
          <button type="button" onClick={load} className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary rounded-lg">
            <RefreshCw size={17} /> Refresh
          </button>
        </div>
      </div>

      <div className={`bg-card border border-border rounded-xl grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 ${compact ? "p-2" : "p-4"}`}>
        <select value={filters.customer_id} onChange={(event) => updateFilter("customer_id", event.target.value)} className="bg-secondary border border-border rounded-lg px-3 py-2 text-sm">
          <option value="">All Customers</option>
          {(options.customers || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select value={filters.manufacturer_id} onChange={(event) => updateFilter("manufacturer_id", event.target.value)} className="bg-secondary border border-border rounded-lg px-3 py-2 text-sm">
          <option value="">All Manufacturers</option>
          {(options.manufacturers || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select value={filters.isr} onChange={(event) => updateFilter("isr", event.target.value)} className="bg-secondary border border-border rounded-lg px-3 py-2 text-sm">
          <option value="">All Sales Ops</option>
          {(options.isr || []).map((name) => <option key={name}>{name}</option>)}
        </select>
        <select value={filters.osr} onChange={(event) => updateFilter("osr", event.target.value)} className="bg-secondary border border-border rounded-lg px-3 py-2 text-sm">
          <option value="">All OSRs</option>
          {(options.osr || []).map((name) => <option key={name}>{name}</option>)}
        </select>
        <select value={filters.phase} onChange={(event) => updateFilter("phase", event.target.value)} className="bg-secondary border border-border rounded-lg px-3 py-2 text-sm">
          <option value="">All Phases</option>
          {data.phases.map((phase) => <option key={phase.id} value={phase.id}>{phase.label}</option>)}
        </select>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-3 py-2 shadow-lg">
          <div className="mr-auto flex items-center gap-2 text-sm font-medium">
            <CheckSquare2 size={17} />
            {selectedProjects.length} project{selectedProjects.length === 1 ? "" : "s"} selected
          </div>
          <button type="button" onClick={groupSelectedProjects} disabled={groupBusy || selectedProjects.length < 2} className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-40" title="Group selected projects (Alt+G)">
            <Layers3 size={15} /> Group <span className="opacity-70">Alt+G</span>
          </button>
          <button type="button" onClick={ungroupSelectedProjects} disabled={groupBusy || !selectedHasGroup} className="inline-flex items-center gap-2 rounded-lg bg-secondary px-3 py-2 text-xs font-semibold disabled:opacity-40" title="Ungroup selected projects (Alt+U)">
            <Ungroup size={15} /> Ungroup <span className="opacity-70">Alt+U</span>
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={archiveSelectedProjects}
              disabled={groupBusy || selectedProjects.length < 1}
              data-project-archive-role="graphics_admin-kanban"
              className="inline-flex items-center gap-2 rounded-lg bg-amber-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-40"
              title="Archive selected projects (Graphics Admin)"
            >
              Archive Selected
            </button>
          )}
          <button type="button" onClick={() => setSelectedIds([])} className="rounded-lg p-2 hover:bg-secondary" title="Clear selection" aria-label="Clear selected projects">
            <X size={16} />
          </button>
        </div>
      )}

      <div className={compact ? "flex gap-2 overflow-x-auto pb-2" : "flex gap-4 overflow-x-auto pb-4"} style={{ minHeight: "72vh" }}>
        {visiblePhases.map((phase) => {
          const cards = scopeProjects.filter((project) => project.workflow_phase === phase.id);
          const arrangedCards = arrangeCards(cards);
          const color = prefs.colors[phase.id] || "#334155";
          const isIntakePhase = phase.id === INTAKE_PHASE_ID;

          return (
            <section
              key={phase.id}
              className={`flex-shrink-0 rounded-xl border ${compact ? "w-60 p-1.5" : "w-72 p-3"}`}
              style={{ backgroundColor: `${color}33`, borderColor: color }}
              onDragOver={(event) => { if (canChangeProjectStatus && !isIntakePhase) event.preventDefault(); }}
              onDrop={(event) => { event.preventDefault(); if (!isIntakePhase) move(dragged, phase.id); }}
            >
              <div className={`flex items-center justify-between ${compact ? "mb-1 px-1" : "mb-3"}`}>
                <h2 className={compact ? "text-[10px] font-bold uppercase tracking-wide" : "text-xs font-bold uppercase tracking-wider"}>{phase.label}</h2>
                <span className="text-[10px] bg-background/60 rounded-full px-1.5 py-0.5">{cards.length}</span>
              </div>

              <div className={compact ? "space-y-0.5" : "space-y-3"}>
                {arrangedCards.map(({ project, showGroupHeader, phaseGroupCount }) => {
                  if (project.card_type === "intake") {
                    return (
                      <article key={`intake-${project.id}`} className={`bg-card border border-amber-700/70 border-l-4 border-l-amber-500 rounded-md ${compact ? "px-2 py-1.5" : "p-3"}`}>
                        <Link to={project.review_path || `/intake-submissions/${project.id}/review?from=kanban`} className="block">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-[9px] font-bold uppercase tracking-wide text-amber-400">Customer Intake</span>
                            <span className="text-[9px] text-muted-foreground">{project.confirmation_number}</span>
                          </div>
                          <p className={compact ? "truncate text-[10px] font-semibold leading-3" : "font-semibold text-sm leading-5"}>{project.customer_name || project.name}</p>
                          <div className={compact ? "mt-1 text-[9px] leading-3 text-muted-foreground" : "mt-2 text-xs text-muted-foreground space-y-1"}>
                            <p className="truncate">{project.manufacturer_name || "No manufacturer"} · {project.can_size || "No size"}</p>
                            <p className="truncate">{project.sku_name || `${project.sku_count || 0} SKU(s)`}</p>
                            <p className="truncate">Artwork PO: {project.artwork_po || "Missing"}</p>
                            <p className="truncate">Sales Ops: {project.isr || "Unassigned"}</p>
                            <p className="flex items-center gap-1 text-amber-400"><Clock3 size={compact ? 10 : 12} /> {waitingAge(project.waiting_since)}</p>
                          </div>
                        </Link>
                        <div className={`flex gap-1.5 ${compact ? "mt-1.5" : "mt-3"}`}>
                          <Link to={project.review_path || `/intake-submissions/${project.id}/review?from=kanban`} className="flex flex-1 items-center justify-center gap-1 rounded bg-secondary px-2 py-1.5 text-[10px] font-medium">
                            <Eye size={11} /> Review
                          </Link>
                          <button type="button" onClick={() => createProjects(project)} disabled={busyIntakeId === project.id} className="flex flex-1 items-center justify-center gap-1 rounded bg-emerald-700 px-2 py-1.5 text-[10px] font-medium text-white disabled:opacity-50">
                            <SplitSquareHorizontal size={11} />
                            {busyIntakeId === project.id ? "Creating..." : "Approve & Create Projects"}
                          </button>
                        </div>
                      </article>
                    );
                  }

                  const archiveHint = productionArchiveHint(project);
                  const hasCustomColor = Boolean(project.kanban_color);
                  const colorEditorOpen = colorProjectId === project.id;
                  const tagEditorOpen = tagProjectId === project.id;
                  const selected = selectedIds.includes(project.id);
                  const selectable = canSelectProject(project);
                  const groupColorKey = project.kanban_group_id || project.intake_group_id;

                  return (
                    <div key={project.id} className={compact ? "space-y-0.5" : "space-y-2"}>
                      {showGroupHeader && (
                        <div className="flex items-center gap-1.5 rounded-md border border-border/70 bg-background/50 px-2 py-1 text-[9px] text-muted-foreground">
                          <Link2 size={11} className="shrink-0" />
                          <span className="min-w-0 flex-1 truncate font-semibold text-foreground">{project.kanban_group_name}</span>
                          <span className="shrink-0">{phaseGroupCount}/{groupTotals[project.kanban_group_id] || phaseGroupCount}</span>
                        </div>
                      )}

                      <article
                        draggable={canChangeProjectStatus && !selected}
                        onDragStart={() => setDragged(project)}
                        onClickCapture={(event) => {
                          if ((event.ctrlKey || event.metaKey) && selectable) {
                            event.preventDefault();
                            event.stopPropagation();
                            toggleProjectSelection(project);
                          }
                        }}
                        className={`relative bg-card border border-l-4 ${selected ? "border-primary ring-2 ring-primary/70" : "border-border"} ${hasCustomColor ? "" : groupClass(groupColorKey)} rounded-md ${compact ? "px-1.5 py-1" : "p-3"}`}
                        style={projectCardStyle(project)}
                      >
                        <div className="flex items-start gap-1">
                          <Link to={`/projects/${project.id}`} className="block min-w-0 flex-1">
                            <div className="flex justify-between gap-1">
                              <p className={compact ? "truncate text-[10px] font-medium leading-3" : "font-medium text-sm"}>{project.name}</p>
                              {project.intake_group_count > 1 && <span className="shrink-0 text-[9px] bg-secondary rounded px-1">{project.intake_group_index}/{project.intake_group_count}</span>}
                            </div>
                            {archiveHint && (
                              <p data-testid="queue-archive-hint" title="Estimated from production start; authoritative archive date is on the project detail page" className={`mt-1 inline-flex items-center gap-1 text-[10px] ${archiveHint.review ? "text-amber-500" : "text-muted-foreground"}`}>
                                <Clock3 size={10} /> {archiveHint.label}
                              </p>
                            )}
                            {project.kanban_group_name && (
                              <p className="mt-0.5 flex items-center gap-1 truncate text-[8px] font-medium text-primary/90">
                                <Link2 size={9} className="shrink-0" /> {project.kanban_group_name}
                              </p>
                            )}
                            {compact ? (
                              <>
                                <p className="truncate text-[9px] leading-3 text-muted-foreground">{project.customer_name || "No customer"}</p>
                                <StatusTagBadge value={project.sub_status} compact />
                              </>
                            ) : (
                              <div className="mt-2 text-xs text-muted-foreground space-y-1">
                                <p>{project.customer_name || "No customer"}</p>
                                <p>{project.manufacturer_name || "—"} · {project.can_size || "—"}</p>
                                <p>{project.sku_name || project.sku_flavor || "—"}</p>
                                <StatusTagBadge value={project.sub_status} />
                              </div>
                            )}
                          </Link>

                          {canChangeProjectStatus && (
                            <div className="flex shrink-0 items-center gap-0.5">
                              {selectable && (
                                <button
                                  type="button"
                                  draggable={false}
                                  onMouseDown={(event) => event.stopPropagation()}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleProjectSelection(project);
                                  }}
                                  className={`rounded p-1 hover:bg-background/60 ${selected ? "text-primary" : "text-muted-foreground"} ${compact ? "-mt-0.5" : ""}`}
                                  title={selected ? "Remove from selection" : "Select for grouping (Ctrl+Click also works)"}
                                  aria-label={`${selected ? "Deselect" : "Select"} ${project.name}`}
                                >
                                  {selected ? <CheckSquare2 size={compact ? 12 : 15} /> : <Square size={compact ? 12 : 15} />}
                                </button>
                              )}
                              <button
                                type="button"
                                draggable={false}
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setTagProjectId((current) => current === project.id ? null : project.id);
                                  setColorProjectId(null);
                                }}
                                className={`rounded p-1 hover:bg-background/60 ${compact ? "-mt-0.5" : ""}`}
                                title="Edit status tag"
                                aria-label={`Edit status tag for ${project.name}`}
                              >
                                <Tag size={compact ? 12 : 15} />
                              </button>
                              <button
                                type="button"
                                draggable={false}
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setColorProjectId((current) => current === project.id ? null : project.id);
                                  setTagProjectId(null);
                                }}
                                className={`rounded p-1 hover:bg-background/60 ${compact ? "-mr-0.5 -mt-0.5" : ""}`}
                                title="Edit project color"
                                aria-label={`Edit color for ${project.name}`}
                              >
                                <Palette size={compact ? 12 : 15} />
                              </button>
                            </div>
                          )}
                        </div>

                        {tagEditorOpen && (
                          <div className="absolute right-1 top-7 z-50 w-48 rounded-lg border border-border bg-popover p-2 shadow-2xl" onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()} draggable={false}>
                            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Interim Status Tag</p>
                            <InterimTagEditorV53 value={project.sub_status || ""}
                              disabled={savingTagProjectId === project.id}
                              isAdmin={isAdmin}
                              onChange={(tag) => setStatusTag(project, tag)} />
                          </div>
                        )}

                        {colorEditorOpen && (
                          <div className="absolute right-1 top-7 z-40 w-44 rounded-lg border border-border bg-popover p-2 shadow-2xl" onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()} draggable={false}>
                            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Project Color</p>
                            <button
                              type="button"
                              disabled={savingColorProjectId === project.id || !lastUsedColor}
                              onClick={() => setProjectColor(project, lastUsedColor)}
                              className="mb-2 flex w-full items-center gap-2 rounded border border-border bg-secondary px-2 py-1.5 text-left text-[10px] disabled:opacity-40"
                              title={lastUsedColor ? `Apply last used color ${lastUsedColor}` : "No last used color yet"}
                            >
                              <span className="h-5 w-5 shrink-0 rounded border border-white/20" style={{ backgroundColor: lastUsedColor || "transparent" }} />
                              <span className="flex-1">Last Used</span>
                              {lastUsedColor && <span className="font-mono text-[8px] text-muted-foreground">{lastUsedColor}</span>}
                            </button>
                            <div className="grid grid-cols-6 gap-1.5">
                              {PROJECT_COLOR_PRESETS.map((preset) => (
                                <button key={preset} type="button" disabled={savingColorProjectId === project.id} onClick={() => setProjectColor(project, preset)} className="h-5 w-5 rounded border border-white/20 ring-offset-1 hover:ring-2 hover:ring-primary" style={{ backgroundColor: preset }} title={preset} aria-label={`Set project color to ${preset}`} />
                              ))}
                            </div>
                            <label className="mt-2 flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                              Custom
                              <input type="color" value={project.kanban_color || lastUsedColor || "#64748b"} disabled={savingColorProjectId === project.id} onChange={(event) => setProjectColor(project, event.target.value)} className="h-7 w-10 cursor-pointer border-0 bg-transparent p-0" />
                            </label>
                            <button type="button" disabled={savingColorProjectId === project.id || !project.kanban_color} onClick={() => setProjectColor(project, "")} className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded bg-secondary px-2 py-1.5 text-[10px] disabled:opacity-40">
                              <RotateCcw size={11} /> Reset to Automatic
                            </button>
                          </div>
                        )}
                      </article>
                    </div>
                  );
                })}

                {cards.length === 0 && (
                  <div className={`text-center text-xs text-muted-foreground ${compact ? "py-4" : "py-10"}`}>
                    {isIntakePhase ? "No requests awaiting Graphics" : "No projects"}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};
